import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FaArrowLeft, FaArrowRight, FaBolt, FaBus, FaCheck, FaClock, FaCreditCard, FaEnvelopeOpenText, FaGasPump,
  FaGraduationCap, FaLock, FaMapMarkedAlt, FaMoneyBillWave, FaPhoneAlt, FaSitemap, FaTruck, FaUsers,
} from 'react-icons/fa';
import { signInAnonymously, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { customToast } from '../utils/customToast';
import '../site.css';
import './SubscriptionUI.css';
import V3Head from '../site-v3/V3Head';
import {
  AccountUnit, BillingPeriod, PaidPlanId, PlanId,
  ADDON_PRICE, PILOT_DAYS, PILOT_LENGTH_LABEL, SELLABLE_PLAN_DEFS,
  addonRate, annualSaving, bill, billedWord, effectivePlan, fleetBandsWords, fmt, fmtDate, isBandedFleet, isYearSplit, money, normalizePaidPlan, perCycleWhenYearly, perUnitRate,
  perUnitSuffix, pilotBillDate, pilotEndDate, rate, recommendPlan, schoolYearBill, shortPeriod, totalSuffix, unitWord, yearSplitWord,
} from './subscriptionModel';
import Zana from './Zana';
import { activateActionSettings, addonsFromSetup, provisionSelfServeOrg, saveSignup, saveSignupDraft, SelfServePayload } from './selfServeSignup';
import LocationPicker, { LatLng } from './LocationPicker';
import { EDUCATION_SYSTEMS, EducationSystem } from '../utils/educationSystems';
import { validateCoupon, initializeCheckout, isPaymentsConfigured, loadPaystackInline, BillingCadence, Currency, resendActivationSelfServe, sendSignInLinkEmailApi } from '../services/paymentsApi';
import {
  SUPPORT_EMAIL,
  SUPPORT_MAILTO_HREF,
  SUPPORT_PAGE_HREF,
  SUPPORT_PHONE_DISPLAY,
  SUPPORT_PHONE_LOCAL,
  SUPPORT_TEL_HREF,
} from '../utils/companyContact';
import { phoneProblem } from '../utils/phoneRules';

type ProductKey = 'bus' | 'lorry' | 'class';

interface ProductDef {
  label: string;
  short: string;
  icon: React.ReactNode;
  grad: string;
  unit: AccountUnit;
  unitP: string;
  suffix: string;
}

const PRODUCTS: Record<ProductKey, ProductDef> = {
  bus: { label: 'Bus Tracking', short: 'School bus routes — keep parents, drivers & schools in sync.', icon: <FaBus />, grad: 'linear-gradient(135deg,#22c3ee,#2f7cf0)', unit: 'child', unitP: 'children', suffix: '.bus' },
  lorry: { label: 'Lorry & Fleet', short: 'Commercial fleets — personal tracking or live cargo for clients.', icon: <FaTruck />, grad: 'linear-gradient(135deg,#ffb27a,#f5872f)', unit: 'vehicle', unitP: 'vehicles', suffix: '.fleet' },
  class: { label: 'Classroom', short: 'Full school suite — students, teachers, parents, diary & menus.', icon: <FaGraduationCap />, grad: 'linear-gradient(135deg,#9b7cff,#6f4ff0)', unit: 'child', unitP: 'children', suffix: '' },
};

const SETUP_OPTS = [
  { id: 'gps', icon: <FaMapMarkedAlt />, title: 'GPS sensor available', on: 'Pair vehicle sensor data with the app.', off: 'Use phone-based driver tracking.' },
  { id: 'fuel', icon: <FaGasPump />, title: 'Fuel sensor installed', on: 'Use hardware fuel readings where available.', off: 'Use driver logs & route estimates.' },
  { id: 'cargo', icon: <FaUsers />, title: 'Client cargo links', on: 'Share live cargo status with selected clients.', off: 'Keep tracking internal to your team.', fleetOnly: true },
  { id: 'fees', icon: <FaMoneyBillWave />, title: 'School fees module', on: 'Link fee balances to gate app access.', off: "Don't connect school fees.", schoolOnly: true },
] as const;

// Add-ons that bill at +50/unit/cycle (cargo is a delivery mode, not a paid sensor).
const PRICED_ADDONS = ['gps', 'fuel', 'fees'] as const;

// Everything Zana does, shown in the hover panel on the Zana Mode upgrade card.
const ZANA_CAPS = [
  { title: 'Route analytics', desc: 'Pinpoints where you may be losing money and the leanest route to run.' },
  { title: 'Fuel-theft detection', desc: "Detects and finds siphoning against each vehicle's own history." },
  { title: 'Contract management', desc: 'Keeps client contracts, renewals & terms organised in one place.' },
  { title: 'Owner summaries', desc: 'A plain-language briefing of everything happening across your fleet.' },
  { title: 'Ask Zana anything', desc: 'Question your fleet in natural language, any time of day.' },
] as const;

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
// Typical Kenyan school year: term 1 (Jan–Mar), term 2 (May–Jul), term 3 (Sep–Nov); 0 = break.
const SCHOOL_TERMS: number[] = [1, 1, 1, 0, 2, 2, 2, 0, 3, 3, 3, 0];

/** Number input you can actually type in: keeps the raw text while editing
    (so clearing/retyping works, not just the spinner) and clamps on blur. */
function NumField({ label, min, value, onChange }: { label: string; min: number; value: number; onChange: (n: number) => void }) {
  const [text, setText] = useState(String(value));
  // Resync only when the committed value changes from outside this field (e.g. query-param init).
  useEffect(() => { setText((t) => (parseInt(t, 10) === value ? t : String(value))); }, [value]);
  return (
    <div className="fld">
      <label>{label}</label>
      <input
        type="number"
        min={min}
        value={text}
        onChange={(e) => {
          const t = e.target.value;
          setText(t);
          const n = parseInt(t, 10);
          if (!isNaN(n) && n >= min) onChange(n);
        }}
        onBlur={() => {
          const n = parseInt(text, 10);
          const clamped = isNaN(n) ? min : Math.max(min, n);
          onChange(clamped);
          setText(String(clamped));
        }}
      />
    </div>
  );
}

function planTitle(id: string) { return id.charAt(0).toUpperCase() + id.slice(1); }
function normalizedPlan(value: string | null): PaidPlanId {
  return normalizePaidPlan(value);
}

export default function PublicOnboarding() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const intent: 'trial' | 'plan' = searchParams.get('intent') === 'plan' ? 'plan' : 'trial';
  const initialProduct: ProductKey =
    searchParams.get('product') === 'lorry' || searchParams.get('product') === 'lorry-cargo' || searchParams.get('product') === 'lorry-personal'
      ? 'lorry'
      : searchParams.get('product') === 'class' ? 'class' : 'bus';
  const qpSize = parseInt(searchParams.get('size') || '', 10);
  const qpPlan = normalizedPlan(searchParams.get('plan'));
  const qpPeriod = searchParams.get('period') as BillingPeriod | null;

  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const resendCooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [paymentPhase, setPaymentPhase] = useState<'idle' | 'processing' | 'done'>('idle');
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [product, setProduct] = useState<ProductKey>(initialProduct);
  const [educationSystem, setEducationSystem] = useState<EducationSystem>('CBC');
  const [lorryMode, setLorryMode] = useState<'personal' | 'cargo'>(
    searchParams.get('product') === 'lorry-personal' ? 'personal' : 'cargo'
  );
  // The plan the account becomes after the pilot. Picked on step 3 — everything
  // downstream (estimate, upgrades offered, checkout) prices off THIS, never off a
  // guess. Until they touch it we keep following the size-based recommendation; the
  // moment they choose, their choice sticks even if they go back and change size.
  const [plan, setPlan] = useState<PaidPlanId>(qpPlan);
  const [planTouched, setPlanTouched] = useState(!!searchParams.get('plan'));

  const P = PRODUCTS[product];
  const unit = P.unit;            // 'child' | 'vehicle'
  const unitWordP = P.unitP;

  const [period, setPeriod] = useState<BillingPeriod>(
    qpPeriod === 'year' ? 'year'
      : qpPeriod === 'term' || qpPeriod === 'termly' || qpPeriod === 'month' ? qpPeriod
        : shortPeriod(PRODUCTS[initialProduct].unit)
  );
  /** Checkout currency — sticky on the org after payment (KES or USD). */
  const [currency, setCurrency] = useState<Currency>('KES');

  const [orgName, setOrgName] = useState('');
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  // The org's home location (school/depot gate). Required at signup — a user-placed
  // pin we may store forever, used later for geofencing with no per-use API call.
  const [homeLocation, setHomeLocation] = useState<LatLng | null>(null);
  const [size, setSize] = useState<number>(!isNaN(qpSize) && qpSize > 0 ? qpSize : (PRODUCTS[initialProduct].unit === 'child' ? 280 : 12));
  const [fleetCount, setFleetCount] = useState(PRODUCTS[initialProduct].unit === 'child' ? 6 : 9);
  const [parents, setParents] = useState(240);
  // monthTerm[i] = 0 (break) | 1 | 2 | 3 — which term each month belongs to.
  const [monthTerm, setMonthTerm] = useState<number[]>([...SCHOOL_TERMS]);
  const [activeTerm, setActiveTerm] = useState<1 | 2 | 3>(1);
  const [termStartDate, setTermStartDate] = useState("");

  const [setup, setSetup] = useState({ gps: true, fuel: false, cargo: product === 'lorry', fees: false });
  // Smart upgrades on the Setup step. Zana Mode needs Premium; running several
  // campuses needs Enterprise (which bundles Zana). Only ever offered when they'd
  // actually raise the picked plan, and mutually exclusive — the account lands on
  // exactly one plan, so two competing "upgrade to…" switches can't both be on.
  const [zanaMode, setZanaMode] = useState(false);
  const [multiBranch, setMultiBranch] = useState(false);
  const [zanaCapsOpen, setZanaCapsOpen] = useState(false);

  const [couponCode, setCouponCode] = useState('');
  const [couponInfo, setCouponInfo] = useState<{ valid: boolean; amountKES?: number } | null>(null);
  const [couponChecking, setCouponChecking] = useState(false);
  const [showPromo, setShowPromo] = useState(false);

  async function checkCoupon() {
    const code = couponCode.trim();
    if (!code) { setCouponInfo(null); return; }
    setCouponChecking(true);
    setCouponInfo(await validateCoupon(code));
    setCouponChecking(false);
  }

  // Keep billing period valid when product (and therefore unit) changes.
  useEffect(() => {
    setPeriod((pr) => (pr === 'year' ? 'year' : shortPeriod(unit)));
    setSetup((s) => ({ ...s, cargo: product === 'lorry' ? s.cargo : false, fees: product === 'lorry' ? false : s.fees }));
  }, [product, unit]);

  useEffect(() => () => {
    if (resendCooldownRef.current) clearInterval(resendCooldownRef.current);
  }, []);

  // What we'd suggest for this size — shown as a "Best fit" badge on the plan cards
  // and used as the default until they pick for themselves.
  const recommended = recommendPlan(unit, size);
  const pickedPlan: PaidPlanId = planTouched ? plan : recommended;
  // The plan the account actually lands on: what they picked, raised by any Setup
  // upgrade they switched on. This is the ONLY thing the estimate and checkout price
  // off — no more quoting one plan while the buyer chose another.
  const billPlan: PlanId = effectivePlan(pickedPlan, { zanaMode, multiBranch });
  // Count enabled priced add-ons (fees only counts for schools). Drives the +100/unit/cycle line.
  const addonCount = PRICED_ADDONS.filter((k) => (setup as Record<string, boolean>)[k] && !(k === 'fees' && product === 'lorry')).length;
  const total = bill(billPlan, size, unit, period, addonCount);
  const perUnitAll = perUnitRate(billPlan, unit, addonCount, { size });
  // A fleet is priced in bands (pricing.js, 2026-10-29), so its per-vehicle
  // figure is an average and its sum is the bands, never "rate × vehicles".
  const avgWord = isBandedFleet(billPlan, unit) ? ' on average' : '';
  const isSchool = unit === 'child';
  // A rate is quoted in its own unit — per term for a school, per month for a
  // lorry — whatever the billing cycle. A school's quarterly or monthly share of a
  // term is not a whole shilling (650 a term is 487.50 a quarter), so the total
  // carries the cycle and the note under it names the year it was split from.
  const shortWord = isSchool ? 'term' : 'mo';

  // Two different dates, and the copy must not blur them: the pilot runs free to
  // pilotEnds, and the first charge lands the day after (firstBill).
  const pilotEnds = useMemo(() => fmtDate(pilotEndDate()), []);
  const firstBill = useMemo(() => fmtDate(pilotBillDate()), []);

  // Upgrades are only meaningful below the plan they'd unlock — pick Enterprise and
  // both switches vanish, so clear them rather than leaving a hidden toggle set.
  // Zana is included in Premium/Enterprise — no separate Basic→Premium upsell.
  const zanaOffered = false;
  const branchOffered = pickedPlan !== 'enterprise';
  useEffect(() => {
    if (!zanaOffered) setZanaMode(false);
    if (!branchOffered) setMultiBranch(false);
  }, [zanaOffered, branchOffered]);

  const STEPS = [
    'Account', 'Organization',
    intent === 'trial' ? 'Plan after pilot' : 'Your plan',
    'Setup', intent === 'trial' ? 'Pilot & price' : 'Review', 'Payment',
  ];
  const lastStep = 6;

  const orgNameCode = orgName.trim().toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
  const productCode = orgNameCode || 'ORG';
  const fullCode = product === 'lorry'
    ? productCode + (lorryMode === 'cargo' ? '.cargo' : '.fleet')
    : productCode + P.suffix;

  const activeCount = monthTerm.filter((t) => t > 0).length;
  const termCount = new Set(monthTerm.filter((t) => t > 0)).size;

  // --- validation: the Organization step holds the only required free-text data ---
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  // 2026-09-30: a real Kenyan number, not just a non-empty box (utils/phoneRules).
  const phoneErr = phone.trim() ? phoneProblem(phone, '+254') : null;
  const orgStepValid = !!(orgNameCode && contactName.trim() && emailOk && phone.trim() && !phoneErr && size >= 1 && fleetCount >= 1 && homeLocation && (product === "lorry" || /^\d{4}-\d{2}-\d{2}$/.test(termStartDate)));

  function buildPayload(): SelfServePayload {
    return {
      product, lorryMode, code: fullCode,
      orgName: orgName.trim(), contactName: contactName.trim(),
      email: email.trim().toLowerCase(), phone: phone.trim(),
      size, fleetCount, parents,
      plan: billPlan as PaidPlanId, period, intent,
      zanaMode, multiBranch,
      // The org home location (school/depot gate) — required at signup.
      homeLocations: homeLocation,
      addons: addonsFromSetup({ gps: setup.gps, fuel: setup.fuel, fees: setup.fees }),
      educationSystem: product === 'lorry' ? undefined : educationSystem,
      // The school's term calendar (monthTerm[i] = 0 break | 1|2|3 term). Persisted so
      // billing can charge on real term boundaries instead of a flat 91-day cycle.
      termCalendar: product === 'lorry' ? undefined : monthTerm,
      currentTermStartDate: product === 'lorry' ? undefined : termStartDate,
      currentTermEndDate: null,
    };
  }

  async function dispatchEmailLink(payload: SelfServePayload): Promise<boolean> {
    try {
      // Carry the signup payload INSIDE the link (?d=token) so it survives any browser/account.
      const settings = activateActionSettings(payload);
      const branded = await sendSignInLinkEmailApi({
        email: payload.email,
        continueUrl: settings.url,
      });
      if (!branded.ok) {
        customToast.show(branded.error || 'Could not send the email. Please try again.', 'error');
        return false;
      }
      return true;
    } catch (e: any) {
      customToast.show(e?.message || 'Could not send the email. Please try again.', 'error');
      return false;
    }
  }

  async function handleStart() {
    // Hard gate: the payment button must never run against an incomplete signup —
    // rail-jumping here used to reach a fake "Account created" screen whose Resend
    // button emailed a real activation link with NO payment taken.
    if (!orgStepValid) {
      go(2);
      customToast.show('Please complete your organization details first.', 'error');
      return;
    }
    if (!termsAccepted) {
      customToast.show('Please accept the Terms of Service to continue.', 'error');
      return;
    }

    setSending(true);
    setPaymentPhase('processing');

    // Provision the org as PENDING. The login email is NOT sent here — it goes out
    // from the payments webhook AFTER Paystack confirms payment
    // (pear-payments/activationEmail.js), so no account goes live unpaid.
    const payload = buildPayload();
    saveSignup(payload);
    void saveSignupDraft(payload); // fire-and-forget: cross-browser fallback stored in Firestore
    if (!auth.currentUser) {
      try {
        const { user: anonUser } = await signInAnonymously(auth);
        const result = await provisionSelfServeOrg(anonUser, payload, true); // pendingActivation=true
        if (!result.ok) {
          // Non-fatal: the payments backend recreates the pending org from the signup
          // draft at checkout. But log the precise reason — a swallowed failure here is
          // exactly what made checkout mysteriously 404 before.
          console.warn('[onboarding] client org provisioning failed (backend self-heals at checkout):', result.error);
        }
        await signOut(auth);
      } catch (e) {
        // signInAnonymously threw — almost always because Anonymous sign-in is disabled
        // on the Firebase project. Non-fatal (the backend recreates the org at checkout),
        // but surface the exact cause instead of swallowing it silently.
        const code = (e as { code?: string })?.code;
        console.warn(
          `[onboarding] anonymous provisioning failed (${code || 'unknown'}) — backend will ` +
          'recreate the pending org at checkout.' +
          (code === 'auth/operation-not-allowed'
            ? ' Fix: enable Anonymous sign-in in Firebase Auth → Sign-in method.'
            : ''),
        );
      }
    }

    // Hand off to Paystack's hosted checkout. Pilot = a small refundable verification
    // to save the card; paid = the real charge. The button keeps showing
    // "Processing payment…" until the redirect — no email is sent on this path, so
    // no "Sending email…" phase either.
    if (isPaymentsConfigured()) {
      try {
        // 'termly' charges on the school's painted term dates; 'term' is a flat quarter;
        // lorry 'month' MUST map to monthly — collapsing it to quarterly charged fleets
        // 3× their on-screen quote. A lorry never has a quarterly on screen (a typed
        // ?period=term still prices it as a month), so anything short is monthly.
        const cadence: BillingCadence =
          period === 'year' ? 'yearly'
            : period === 'month' || !isSchool ? 'monthly'
              : period === 'termly' ? 'termly'
                : 'quarterly';
        const res = await initializeCheckout({
          orgCode: fullCode,
          email: email.trim(),
          plan: billPlan as 'premium' | 'enterprise',
          unit, size, addonCount, cadence, currency,
          couponCode: couponCode.trim() || undefined,
          pilot: intent === 'trial',
        });

        // Open Paystack's SECURE modal over our split-screen, resuming the server-
        // initialized transaction (access_code) so the amount stays server-set. On
        // success we control the outcome (onSuccess) and show the "check your email"
        // screen ourselves — no getting stranded on Paystack. (Paystack's inline embed
        // can't be used here: it requires a client-side amount, which we won't trust.)
        const PaystackPop = res.access_code ? await loadPaystackInline() : null;
        const onPaid = () => {
          setSending(false); setPaymentPhase('idle');
          setSubmitted(true); window.scrollTo(0, 0); // → "Account created — check your email"
        };
        const onDismiss = () => { setSending(false); setPaymentPhase('idle'); };
        if (PaystackPop) {
          try {
            new PaystackPop().resumeTransaction(res.access_code, {
              onSuccess: onPaid,
              onCancel: onDismiss,
              onError: (err: { message?: string }) => { onDismiss(); customToast.show(`Payment error: ${err?.message || 'please try again'}.`, 'error'); },
            });
            return;
          } catch (modalErr) {
            console.warn('[onboarding] modal checkout failed, using hosted redirect:', modalErr);
          }
        }

        // Last resort → full-page hosted checkout (redirects to /billing/return).
        window.location.href = res.authorization_url;
        return;
      } catch (err) {
        // Payment init failed — do NOT silently show the success screen (that hides a
        // real backend/Paystack error). Surface the ACTUAL reason and keep them here to
        // retry, instead of a generic "check your connection" that misdiagnoses the cause.
        const reason = err instanceof Error && err.message ? err.message : 'unexpected error';
        console.error('[onboarding] Paystack checkout init failed:', err);
        setSending(false);
        setPaymentPhase('idle');
        customToast.show(
          `Couldn't start the payment: ${reason}. Please try again, or email ${SUPPORT_EMAIL} / call ${SUPPORT_PHONE_LOCAL}.`,
          'error',
        );
        return;
      }
    }

    // Payments backend not wired into this build (REACT_APP_PAYMENTS_URL unset).
    // In production that's an outage, not a free-account coupon — fail loudly.
    if (process.env.NODE_ENV === 'production') {
      console.error('[onboarding] REACT_APP_PAYMENTS_URL unset in a production build — refusing unpaid signup.');
      setSending(false);
      setPaymentPhase('idle');
      customToast.show('Payments are temporarily unavailable. Please try again shortly, or contact support.', 'error');
      return;
    }

    // Dev/mock only: no webhook exists to send the login link after payment, so email
    // it directly. NO charge is taken in this path.
    console.warn(
      '[onboarding] Payments backend not configured (REACT_APP_PAYMENTS_URL unset) — ' +
      'emailing the login link directly; NO payment taken (dev builds only).',
    );
    setPaymentPhase('done'); // "Sending email…" — truthful: the email is being sent right now
    const ok = await dispatchEmailLink(payload);
    setSending(false);
    setPaymentPhase('idle');
    if (ok) {
      setSubmitted(true);
      window.scrollTo(0, 0);
    }
  }

  function startResendCooldown() {
    if (resendCooldownRef.current) clearInterval(resendCooldownRef.current);
    setResendCooldown(60);
    resendCooldownRef.current = setInterval(() => {
      setResendCooldown((s) => {
        if (s <= 1) {
          if (resendCooldownRef.current) {
            clearInterval(resendCooldownRef.current);
            resendCooldownRef.current = null;
          }
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  }

  async function handleResend() {
    if (resendCooldown > 0) return;
    setSending(true);
    let ok = false;
    if (isPaymentsConfigured()) {
      const result = await resendActivationSelfServe(fullCode, email.trim().toLowerCase());
      ok = result.ok;
      if (result.ok) {
        customToast.show(
          'Link sent again — check your inbox for an email from PEAR Fleet (not a generic Firebase message).',
          'success',
        );
        startResendCooldown();
      } else {
        customToast.show(result.message || 'Could not resend the link. Please try again.', 'error');
      }
    } else {
      // Dev/mock only — no payments backend, so Firebase client email is the only path.
      ok = await dispatchEmailLink(buildPayload());
      if (ok) {
        customToast.show('Link sent again — check your inbox.', 'success');
        startResendCooldown();
      }
    }
    setSending(false);
  }

  function go(n: number) {
    const target = Math.max(1, Math.min(lastStep, n));
    // The rail is clickable, but steps past Organization need valid org details —
    // otherwise a rail-jump lands on the payment step with an empty signup.
    if (target > 2 && !orgStepValid) { setStep(2); return; }
    setStep(target);
  }
  // Paint the clicked month with the active term, or clear it if it already holds that term.
  function paintMonth(i: number) { setMonthTerm((m) => m.map((v, idx) => (idx === i ? (v === activeTerm ? 0 : activeTerm) : v))); }

  const periodToggle = (
    <div className="seg seg-period">
      {isSchool ? (
        <>
          <button className={period === 'termly' ? 'on' : ''} onClick={() => setPeriod('termly')} title="Billed at the start of each term you marked on your calendar">Per term</button>
          <button className={period === 'month' ? 'on' : ''} onClick={() => setPeriod('month')} title="A year of three terms, split into 12 monthly payments">Monthly</button>
          <button className={period === 'term' ? 'on' : ''} onClick={() => setPeriod('term')} title="A year of three terms, split into 4 payments, one every 91 days, regardless of your calendar">Quarterly</button>
          <button className={period === 'year' ? 'on' : ''} onClick={() => setPeriod('year')}>Per year · -10%</button>
        </>
      ) : (
        <>
          <button className={period === 'month' ? 'on' : ''} onClick={() => setPeriod('month')}>Monthly</button>
          <button className={period === 'year' ? 'on' : ''} onClick={() => setPeriod('year')}>Annual · -10%</button>
        </>
      )}
    </div>
  );

  const currencyToggle = (
    <div className="seg seg-period" role="group" aria-label="Billing currency">
      <button
        type="button"
        className={currency === 'KES' ? 'on' : ''}
        onClick={() => setCurrency('KES')}
        title="Kenyan shillings — settles to our Equity KES account once Paystack is configured"
      >
        KES
      </button>
      <button
        type="button"
        className={currency === 'USD' ? 'on' : ''}
        onClick={() => setCurrency('USD')}
        title="US dollars — for outside clients; settles to our Equity USD account once Paystack is configured"
      >
        USD
      </button>
    </div>
  );

  /* ---------------- steps ---------------- */
  function renderStep() {
    if (step === 1) return (
      <>
        <div className="fm-head">
          <h1>What are you setting up?</h1>
          <p>Each path tailors the questions, the live demo, and how your plan is sized.</p>
        </div>
        <div className="choice-grid">
          {(Object.keys(PRODUCTS) as ProductKey[]).map((key) => {
            const pr = PRODUCTS[key];
            return (
              <button key={key} className={`choice${product === key ? ' on' : ''}`} type="button" onClick={() => setProduct(key)}>
                <span className="ch-ic" style={{ background: pr.grad }}>{pr.icon}</span>
                <strong>{pr.label}</strong>
                <small>{pr.short}</small>
              </button>
            );
          })}
        </div>
        {product === 'lorry' && (
          <div className="subchoice">
            <div className="scl">What does this fleet need?</div>
            <div className="subrow">
              <button className={`subbtn${lorryMode === 'personal' ? ' on' : ''}`} type="button" onClick={() => setLorryMode('personal')}>Personal tracking</button>
              <button className={`subbtn${lorryMode === 'cargo' ? ' on' : ''}`} type="button" onClick={() => setLorryMode('cargo')}>Cargo + clients</button>
            </div>
            <div className="subnote">
              {lorryMode === 'personal'
                ? <>Owner + driver self-tracking. Code suffix <code>.fleet</code></>
                : <>Live maps shared with cargo clients. Code suffix <code>.cargo</code></>}
            </div>
          </div>
        )}
      </>
    );

    if (step === 2) return (
      <>
        <div className="fm-head">
          <h1>Tell us about your {product === 'class' ? 'school' : product === 'lorry' ? 'fleet' : 'organization'}</h1>
          <p>We'll use this to set up your account and size your plan. Nothing is charged yet.</p>
        </div>
        <div className="fld-grid">
          <div className="fld full">
            <label>{product === 'lorry' ? 'Company / fleet name' : 'School / organization name'}</label>
            <input autoComplete="organization" value={orgName} onChange={(e) => setOrgName(e.target.value)}
              placeholder={initialProduct === 'bus' ? 'e.g. Green Valley Academy' : initialProduct === 'lorry' ? 'e.g. Sokoni Haulage' : 'e.g. Brookside School'} />
            <span className="hint">Your code will be <code style={{ fontFamily: 'Geist Mono, monospace', color: 'var(--brand-ink)', fontWeight: 700 }}>{fullCode}</code></span>
          </div>
          <div className="fld"><label>Contact name</label><input autoComplete="name" value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="e.g. Grace Mwangi" /></div>
          <div className="fld">
            <label>Work email</label>
            <input autoComplete="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ops@yourorg.com" />
            <span className="hint">This becomes your admin login.{email.trim() && !emailOk && <b style={{ color: '#d3455b' }}> Enter a valid email.</b>}</span>
          </div>
          <div className="fld"><label>Phone</label><input autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+254 700 000 000" />
            {phoneErr && <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 4 }}>{phoneErr}</div>}
          </div>
          <NumField label={isSchool ? 'Children to track' : 'Vehicles to track'} min={1} value={size} onChange={setSize} />
          <NumField label={isSchool ? 'Buses used' : 'Drivers'} min={1} value={fleetCount} onChange={setFleetCount} />
          {product !== 'lorry' && (
            <NumField label="Parents to invite" min={0} value={parents} onChange={setParents} />
          )}
          {product !== 'lorry' && (
            <div className="fld">
              <label>Curriculum / system</label>
              <select value={educationSystem} onChange={(e) => setEducationSystem(e.target.value as EducationSystem)}>
                {EDUCATION_SYSTEMS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <span className="hint">Parents pick their child's year group from this system; it also drives year-end promotion.</span>
            </div>
          )}
        </div>

        <div className="fld full" style={{ marginTop: 18 }}>
          <label>{product === 'lorry' ? 'Depot / yard location' : 'School location'}</label>
          <LocationPicker
            value={homeLocation}
            onChange={setHomeLocation}
            label={`Drop a pin on your ${product === 'lorry' ? 'depot gate' : 'school gate'} — this is your home base for tracking.`}
          />
          <span className="hint">
            {homeLocation
              ? 'Location set. Drag the pin to fine-tune.'
              : <b style={{ color: '#d3455b' }}>Required — tap the map to place your location.</b>}
          </span>
        </div>

        {isSchool && (
          <div className="months">
            <div className="months-head">
              <span className="mh-title">When is your school in session?</span>
              <span className="mh-sub">Pick a term, then tap the months it runs. We bill per term, only for terms you run.</span>
            </div>
            <div className="term-pick">
              <span className="tp-label">Marking</span>
              {[1, 2, 3].map((t) => (
                <button key={t} type="button" className={`tp t${t}${activeTerm === t ? ' on' : ''}`} onClick={() => setActiveTerm(t as 1 | 2 | 3)}>
                  <i /> Term {t}
                </button>
              ))}
            </div>
            <div className="months-grid">
              {MONTHS.map((m, i) => {
                const t = monthTerm[i];
                return (
                  <button key={m} type="button" className={`month-chip${t ? ` on t${t}` : ''}`} onClick={() => paintMonth(i)}>
                    {m.slice(0, 3)}
                    <small>{t ? `Term ${t}` : 'Break'}</small>
                  </button>
                );
              })}
            </div>
            <div className="months-foot">
              <span className="mf-pill">{activeCount} months · {termCount} term{termCount === 1 ? '' : 's'} a year</span>
              <button type="button" onClick={() => setMonthTerm([...SCHOOL_TERMS])}>Reset to school year</button>
              <button type="button" onClick={() => setMonthTerm(Array(12).fill(0))}>Clear</button>
            </div>
            <div className="fld" style={{ marginTop: 16 }}>
              <label>When did this term start? *</label>
              <input
                type="date"
                value={termStartDate}
                onChange={(e) => setTermStartDate(e.target.value)}
                required
              />
              <span className="hint">
                The first day children came back <b>this</b> term — not the whole year.
                Fuel and route charts start from this day. When this term is nearly over we will
                ask for its last day and the first day of the next. Billing still follows the months above.
              </span>
            </div>
          </div>
        )}
      </>
    );

    /* --- 3. the plan the account becomes after the pilot --------------- */
    if (step === 3) {
      const paidPlans = SELLABLE_PLAN_DEFS;
      return (
        <>
          <div className="fm-head">
            <h1>{intent === 'trial' ? 'What should your account become?' : 'Choose your plan'}</h1>
            <p>{intent === 'trial'
              ? <>Your {PILOT_LENGTH_LABEL} pilot runs every feature free until <b>{pilotEnds}</b>. Pick the plan it settles onto after that — the first charge is <b>{firstBill}</b>, and you can change plan any time before then.</>
              : <>This is what {orgName.trim() || 'your account'} is billed for. You can change plan any time from your dashboard.</>}</p>
          </div>

          <div className="plan-pick">
            {paidPlans.map((p) => {
              const id = p.id as PaidPlanId;
              const selected = pickedPlan === id;
              const perUnit = rate(id, unit, { size });
              const banded = isBandedFleet(id, unit);
              const planTotal = bill(id, size, unit, period, addonCount);
              const hasZana = id === 'premium' || id === 'enterprise';
              return (
                <button
                  key={id}
                  type="button"
                  className={`pcard pk${selected ? ' sel' : ''}`}
                  onClick={() => { setPlan(id); setPlanTouched(true); }}
                >
                  <div className="ptop">
                    <span className="pn" style={hasZana ? { color: '#6f4ff0' } : undefined}>{p.name}</span>
                    {selected
                      ? <span className="recpill">✓ Selected</span>
                      : recommended === id ? <span className="recpill recpill-soft">Best fit</span> : null}
                  </div>
                  <div className="pd">{p.desc}</div>

                  <div className="punit">
                    <span className="c">{currency === 'USD' ? 'USD' : 'Ksh'}</span><span className="n">{currency === 'USD' ? money(perUnit, 'USD').replace(/^\$/, '') : fmt(perUnit)}</span>
                    <span className="p">/ {unitWord(unit, 1)} · {perUnitSuffix(unit)}{banded ? ' on average' : ''}</span>
                  </div>

                  <div className="ptotal">
                    <div className="tk">{size.toLocaleString()} {unitWordP} · billed {billedWord(unit, period)}</div>
                    <div className="tv">{money(planTotal, currency)}<span style={{ fontSize: 13, color: 'var(--ink-3)', fontWeight: 600 }}> / {totalSuffix(unit, period)}</span></div>
                    <div className="ty">
                      {addonCount > 0
                        ? <>incl. {addonCount} add-on{addonCount === 1 ? '' : 's'} · {money(addonRate(addonCount), currency)}/{unitWord(unit, 1)}</>
                        : isYearSplit(unit, period)
                          ? <>{money(schoolYearBill(id, size), currency)} a year ÷ {yearSplitWord(period)}</>
                          : banded
                            ? <>Ksh {fleetBandsWords(size)}</>
                            : <>{money(perUnit, currency)} × {size.toLocaleString()} {unitWordP}</>}
                    </div>
                  </div>

                  {hasZana && (
                    <span className="zchip zchip-sm">
                      <Zana state="online" size={16} animated={false} />
                      Zana Mode included
                    </span>
                  )}

                  <ul>
                    {p.feats.map((f) => <li key={f}><FaCheck />{f}</li>)}
                  </ul>
                </button>
              );
            })}
          </div>

          <div className="plan-billrow">
            <span className="pb-label">How would you like to be billed?</span>
            {periodToggle}
          </div>
          <div className="plan-billrow" style={{ marginTop: 10 }}>
            <span className="pb-label">Currency</span>
            {currencyToggle}
          </div>
          <div className="subnote" style={{ marginTop: 12 }}>
            {period === 'termly'
              ? <>You're billed one term at a time, at the start of each of the <b>{termCount || 3} term{termCount === 1 ? '' : 's'}</b> you marked on your calendar — never during a break.</>
              : period === 'year' ? <>One annual payment, 10% off the per-cycle price.</>
                : period === 'month' ? <>Charged every month{isSchool ? ' — a year of three terms, split into 12 equal payments' : ''}.</>
                  : <>A year of three terms, split into 4 equal payments — one every 91 days, regardless of your calendar.</>}
            {' '}Prices convert from our KES catalog; your choice sticks for renewals.
          </div>
        </>
      );
    }

    /* --- 4. hardware / feature setup + the smart upgrades -------------- */
    if (step === 4) {
      const opts = SETUP_OPTS.filter((o) => {
        if ('fleetOnly' in o && (o as { fleetOnly?: boolean }).fleetOnly) return product === 'lorry';
        if ('schoolOnly' in o && (o as { schoolOnly?: boolean }).schoolOnly) return product !== 'lorry';
        return true;
      });
      const unitCycle = isSchool ? 'child · term' : 'vehicle · mo';
      return (
        <>
          <div className="fm-head">
            <h1>How is your fleet set up?</h1>
            <p>This previews how trasse configures tracking once your account is live. Each add-on is <b>+Ksh {ADDON_PRICE} / {unitCycle}</b> and your tech team connects it after signup. You can change any of this later.</p>
          </div>
          <div className="setup-grid">
            {opts.map((o) => {
              const on = setup[o.id as keyof typeof setup];
              const priced = (PRICED_ADDONS as readonly string[]).includes(o.id);
              return (
                <button key={o.id} className={`setup-opt${on ? ' on' : ''}`} type="button" onClick={() => setSetup((s) => ({ ...s, [o.id]: !s[o.id as keyof typeof setup] }))}>
                  <span className="sw"></span>
                  <span className="so-ic">{o.icon}</span>
                  <strong>{o.title}{priced && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 800, color: 'var(--leaf-deep,#2e9e6b)' }}>+{ADDON_PRICE}</span>}</strong>
                  <small>{on ? o.on : o.off}</small>
                </button>
              );
            })}
          </div>

          {/* Smart upgrades — only the ones that would actually RAISE the plan they
              picked. On Enterprise nothing shows: there is nothing left to sell. */}
          {(zanaOffered || branchOffered) && (
            <>
              <div className="upg-div"><span>Smart upgrades</span><i /></div>

              {zanaOffered && (
                <div className="upg-wrap"
                  onMouseEnter={() => setZanaCapsOpen(true)}
                  onMouseLeave={() => setZanaCapsOpen(false)}>
                  <button type="button" className={`upg-card upg-zana${zanaMode ? ' on' : ''}`}
                    onClick={() => { setZanaMode((v) => !v); setMultiBranch(false); }}>
                    <span className="sw"></span>
                    <span className="upg-face"><Zana state={zanaMode ? 'happy' : 'online'} size={62} animated /></span>
                    <span className="upg-body">
                      <span className="upg-title">
                        <strong>Zana Mode</strong>
                        <span className="upg-pill upg-pill-zana">Makes your account Premium</span>
                      </span>
                      <small>Your fleet's AI copilot. Deeper dives, exclusive owner summaries, and everything Zana can do — hover to see it all.</small>
                      <span className="upg-chips">
                        {ZANA_CAPS.map((c) => <span key={c.title} className="upg-chip">{c.title}</span>)}
                      </span>
                    </span>
                  </button>

                  {zanaCapsOpen && (
                    <div className="zcaps">
                      <div className="zcaps-head">
                        <span className="zcaps-ic"><Zana state="online" size={26} animated={false} /></span>
                        <div>
                          <div className="zcaps-t">Everything Zana does for your fleet</div>
                          <div className="zcaps-s">Included on Premium &amp; Enterprise — no metering, no AI credits.</div>
                        </div>
                      </div>
                      <div className="zcaps-grid">
                        {ZANA_CAPS.map((c) => (
                          <div key={c.title} className="zcap">
                            <div className="zcap-t">{c.title}</div>
                            <div className="zcap-d">{c.desc}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {branchOffered && (
                <button type="button" className={`upg-card upg-branch${multiBranch ? ' on' : ''}`}
                  onClick={() => { setMultiBranch((v) => !v); setZanaMode(false); }}>
                  <span className="sw"></span>
                  <span className="upg-ic"><FaSitemap /></span>
                  <span className="upg-body">
                    <span className="upg-title">
                      <strong>Multi-branch {product === 'lorry' ? 'fleet' : 'school'}</strong>
                      <span className="upg-pill upg-pill-ent">Use Enterprise</span>
                      <span className="zchip">
                        <Zana state="online" size={15} animated={false} />
                        Zana Mode included
                      </span>
                    </span>
                    <small>Run several {product === 'lorry' ? 'depots' : 'campuses'} under one account — shared fleet, per-branch dashboards, consolidated billing and org-wide roles.</small>
                  </span>
                </button>
              )}
            </>
          )}

          {addonCount > 0 && (
            <div className="subnote" style={{ marginTop: 22 }}>
              {addonCount} add-on{addonCount === 1 ? '' : 's'} × Ksh {ADDON_PRICE} = <b>+Ksh {fmt(addonRate(addonCount))} / {unitCycle}</b> on top of your plan.
            </div>
          )}
          {billPlan !== pickedPlan && (
            <div className="upg-note">
              <FaBolt />
              {zanaMode
                ? <>Zana Mode moves you from {planTitle(pickedPlan)} to <b>Premium</b> — Ksh {fmt(perUnitAll)} / {unitWord(unit, 1)} · {perUnitSuffix(unit)}{avgWord}.</>
                : <>Multiple branches need <b>Enterprise</b> — Ksh {fmt(perUnitAll)} / {unitWord(unit, 1)} · {perUnitSuffix(unit)}{avgWord}, with Zana Mode included.</>}
            </div>
          )}
        </>
      );
    }

    if (step === 5) return (
      <>
        <div className="fm-head">
          <h1>{intent === 'trial' ? `Your ${PILOT_LENGTH_LABEL} pilot` : `${planTitle(billPlan)} plan`}</h1>
          <p>{intent === 'trial'
            ? `Run a real route or fleet free for ${PILOT_DAYS} days with every feature unlocked. Here's what it becomes afterwards.`
            : 'Confirm the plan you picked. Switch billing to see the difference.'}</p>
        </div>

        <div className="est">
          <div className="est-banner">
            <div>
              <h3>{intent === 'trial' ? `${PILOT_DAYS} days free · all features` : `${planTitle(billPlan)} · Ksh ${fmt(perUnitAll)}/${unit === 'child' ? 'child' : 'vehicle'}/${shortWord}${avgWord}`}</h3>
              <p>{intent === 'trial'
                ? `Free until ${pilotEnds}, first charged ${firstBill}. We save a payment method now so ${orgName.trim() || 'your account'} keeps running the moment the pilot ends.`
                : `${size.toLocaleString()} ${unitWordP} on ${planTitle(billPlan)}, billed ${billedWord(unit, period)}.`}</p>
            </div>
            <span className="eb-tag">{intent === 'trial' ? 'Ksh 0 today' : 'Ready to start'}</span>
          </div>

          <div className="est-card">
            <div className="est-row">
              <h3>{intent === 'trial' ? 'After your pilot' : "What you'll pay"}</h3>
              {periodToggle}
            </div>

            <div className="est-summary">
              <div className="est-tile"><div className="etk">Plan</div><div className="etv">{planTitle(billPlan)}</div></div>
              <div className="est-tile"><div className="etk">Add-ons</div><div className="etv">{addonCount > 0 ? `${addonCount} × ${money(ADDON_PRICE, currency)}` : 'None'}</div></div>
              <div className="est-tile"><div className="etk">Rate</div><div className="etv">{money(perUnitAll, currency)}<span style={{ fontSize: 12, color: 'var(--ink-3)', fontWeight: 600 }}>/{unit === 'child' ? 'child' : 'vehicle'}/{shortWord}{avgWord}</span></div></div>
            </div>

            <div className="est-big">
              <div>
                <div className="ebk">{intent === 'trial' ? `Estimated bill from ${firstBill}` : 'Total'}</div>
                <div className="ebv">{money(total, currency)}<small> / {totalSuffix(unit, period) === 'yr' ? 'year' : totalSuffix(unit, period)}</small></div>
              </div>
              <div className="ebnote">
                {period === 'year'
                  ? <>≈ Ksh {fmt(perCycleWhenYearly(billPlan, size, unit, addonCount))}/{isSchool ? 'term' : 'mo'} · you save Ksh {fmt(annualSaving(billPlan, size, unit, addonCount))}/yr</>
                  : isYearSplit(unit, period)
                    ? <>Ksh {fmt(schoolYearBill(billPlan, size, addonCount))} a year ÷ {yearSplitWord(period)}{addonCount > 0 ? ` (incl. ${addonCount} add-on${addonCount === 1 ? '' : 's'})` : ''}</>
                    : <>{isBandedFleet(billPlan, unit) ? `Ksh ${fleetBandsWords(size)}${addonCount > 0 ? ` + Ksh ${fmt(addonRate(addonCount))} × ${size.toLocaleString()}` : ''}` : `Ksh ${fmt(perUnitAll)} × ${size.toLocaleString()} ${unitWordP}`}{addonCount > 0 ? ` (incl. ${addonCount} add-on${addonCount === 1 ? '' : 's'})` : ''}{period === 'termly' ? ' · billed at each term start' : ''}</>}
              </div>
            </div>

            {intent === 'trial' && (
              <div className="est-upsell">
                <FaBolt />
                {billPlan === pickedPlan
                  ? <>Settles onto <b style={{ margin: '0 4px' }}>{planTitle(billPlan)}</b> — the plan you picked{recommended === billPlan ? `, and our best fit for ${size.toLocaleString()} ${unitWordP}` : ''}. Change plans anytime from your dashboard.</>
                  : <>Settles onto <b style={{ margin: '0 4px' }}>{planTitle(billPlan)}</b> — raised from {planTitle(pickedPlan)} by the {zanaMode ? 'Zana Mode' : 'multi-branch'} upgrade you switched on. Change plans anytime from your dashboard.</>}
              </div>
            )}
          </div>
        </div>
      </>
    );

    return null; // step 6 (payment) renders its own full-screen split, not inside the rail flow
  }

  const railEyebrow = intent === 'trial' ? 'Free pilot setup' : `${planTitle(billPlan)} setup`;
  const railTitle = intent === 'trial' ? `Start your ${PILOT_LENGTH_LABEL} pilot` : 'Set up your plan';
  const railPeriodLabel =
    period === 'year' ? 'Per year'
      : period === 'month' ? 'Per month'
        : period === 'termly' ? 'Per term'
          : isSchool ? 'Per quarter' : 'Per month';

  /* ---------------- nav ---------------- */
  // The trasseapp.com header (2026-10-28) in place of the old site's nav.
  const Nav = <V3Head />;

  /* ---------------- confirmation ---------------- */
  if (submitted) {
    return (
      <div className="site-theme v3" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        {Nav}
        <main style={{ flex: 1 }}>
          <div className="done">
            <div className="done-mark"><FaEnvelopeOpenText /></div>
            <h1>Account created — check your email</h1>
            <p>Your organization <b>{orgName}</b> has been set up. We sent a secure sign-in link to <b>{email}</b> — open it to set your password and step into your {product === 'lorry' ? 'Fleet' : product === 'class' ? 'School' : 'Bus Tracking'} dashboard.</p>
            <div className="done-badge"><FaClock /> The link expires soon · check spam if you don't see it</div>

            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              background: '#f0f5ff', border: '1px solid #c7d9ff',
              borderRadius: 12, padding: '12px 16px', margin: '16px 0',
              textAlign: 'left', fontSize: 14, color: '#1a2e6b',
            }}>
              <span style={{ fontSize: 20, lineHeight: 1, flexShrink: 0 }}>💻</span>
              <span>
                <b>Open the link on this same device and browser.</b>
                {' '}Don't switch to your phone or a different browser —
                just click the email link here on this screen and it will open automatically.
              </span>
            </div>

            <div className="done-summary">
              <div className="dsrow"><span>Product</span><b>{P.label}{product === 'lorry' ? ` · ${lorryMode === 'cargo' ? 'Cargo + clients' : 'Personal'}` : ''}</b></div>
              <div className="dsrow"><span>Organization code</span><b style={{ fontFamily: 'Geist Mono, monospace', color: 'var(--brand-ink)' }}>{fullCode}</b></div>
              <div className="dsrow"><span>Admin email</span><b>{email}</b></div>
              <div className="dsrow"><span>Plan</span><b>{planTitle(billPlan)}{intent === 'trial' ? ' · Pilot' : ''} · {period === 'year' ? 'Annual' : period === 'month' ? 'Monthly' : period === 'termly' ? 'Per term' : isSchool ? 'Quarterly' : 'Monthly'}</b></div>
              <div className="dsrow"><span>{intent === 'trial' ? 'Due today' : 'Total'}</span><b>{money(intent === 'trial' ? 0 : total, currency)}</b></div>
            </div>

            <div className="done-actions">
              <button
                className="btn btn-primary btn-lg"
                disabled={sending || resendCooldown > 0}
                onClick={handleResend}
              >
                {sending ? 'Sending…' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend link'}
              </button>
              <button className="btn btn-ghost btn-lg" onClick={() => setSubmitted(false)}>Use a different email</button>
            </div>

            {/* Self-serve contact path: support page + email + interim company phone (P8). */}
            <p style={{ marginTop: 28, fontSize: 14, color: '#5a6573', lineHeight: 1.55, maxWidth: 420, marginLeft: 'auto', marginRight: 'auto' }}>
              Need a walkthrough or stuck on setup?{' '}
              <a href={SUPPORT_PAGE_HREF} style={{ color: '#2f7cf0', fontWeight: 600 }}>Support</a>
              {' · '}
              <a href={SUPPORT_MAILTO_HREF} style={{ color: '#2f7cf0', fontWeight: 600 }}>{SUPPORT_EMAIL}</a>
              {' · '}
              <a href={SUPPORT_TEL_HREF} style={{ color: '#2f7cf0', fontWeight: 600 }}>{SUPPORT_PHONE_LOCAL}</a>
              <span style={{ display: 'block', marginTop: 4, fontSize: 12.5, color: '#8a93a2' }}>
                Call or WhatsApp {SUPPORT_PHONE_DISPLAY}, Mon–Fri 8:00–18:00 EAT
              </span>
            </p>
          </div>
        </main>
      </div>
    );
  }

  /* ---------------- payment (Stripe-style split screen) ---------------- */
  if (step === lastStep) {
    const due = intent === 'trial' ? 0 : total;
    const billingLabel = period === 'year' ? 'Annual · −10%'
      : period === 'month' ? 'Monthly'
        : period === 'termly' ? 'Per term · your term dates'
          : isSchool ? 'Quarterly' : 'Monthly';
    const addonList = PRICED_ADDONS
      .filter((k) => (setup as Record<string, boolean>)[k] && !(k === 'fees' && product === 'lorry'))
      .map((k) => ({ gps: 'GPS', fuel: 'Fuel', fees: 'Fees' }[k])).join(' · ');
    const sumRow = (label: string, value: React.ReactNode, faint = false) => (
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '12px 0', borderTop: '1px solid #ececf3', fontSize: 14 }}>
        <span style={{ color: '#8a93a2' }}>{label}</span>
        <span style={{ color: faint ? '#8a93a2' : '#1b2430', fontWeight: faint ? 500 : 600, textAlign: 'right' }}>{value}</span>
      </div>
    );
    const busy = paymentPhase !== 'idle';
    const skelBar = (w: string) => <div className="pay-skel" style={{ height: 44, width: w, borderRadius: 10 }} />;

    return (
      <div className="site-theme v3" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <style>{`
          @keyframes payShimmer { 0% { background-position: -420px 0 } 100% { background-position: 420px 0 } }
          .pay-skel { background: linear-gradient(90deg,#eef1f6 25%,#e2e7f0 37%,#eef1f6 63%); background-size: 840px 100%; animation: payShimmer 1.25s ease-in-out infinite; }
          .pay-split { display: grid; grid-template-columns: 1fr 1fr; flex: 1; min-height: 0; }
          @media (max-width: 860px) { .pay-split { grid-template-columns: 1fr; } .pay-split .pay-left { border-right: none !important; border-bottom: 1px solid #ececf3; } }
        `}</style>
        {Nav}
        <div className="pay-split">
          {/* LEFT — the summary, branded & persistent */}
          <div className="pay-left" style={{ background: 'linear-gradient(180deg,#f7f9fc,#f2f5fb)', borderRight: '1px solid #ececf3', padding: '46px clamp(24px,5vw,64px)' }}>
            <div style={{ maxWidth: 440, marginLeft: 'auto', width: '100%' }}>
              <button type="button" onClick={() => go(step - 1)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'none', border: 'none', color: '#5a6573', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: 26 }}>
                <FaArrowLeft style={{ fontSize: 12 }} /> Back
              </button>
              <div style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#8a93a2' }}>
                {intent === 'trial' ? 'Due today' : 'Amount due'}
              </div>
              <div style={{ fontSize: 'clamp(40px,6vw,52px)', fontWeight: 800, letterSpacing: '-.02em', color: '#0f172a', lineHeight: 1.02, margin: '6px 0 4px' }}>
                {money(due, currency)}
              </div>
              {intent === 'trial'
                ? <div style={{ fontSize: 14, color: '#5a6573' }}>then <b style={{ color: '#334155' }}>{money(total, currency)}/{totalSuffix(unit, period)}</b> from {firstBill}</div>
                : <div style={{ fontSize: 14, color: '#5a6573' }}>{planTitle(billPlan)} · billed {billedWord(unit, period)}</div>}

              <div style={{ marginTop: 26 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '2px 0 12px', fontSize: 14 }}>
                  <span style={{ color: '#8a93a2' }}>{product === 'lorry' ? 'Fleet' : 'Organization'}</span>
                  <span style={{ color: '#1b2430', fontWeight: 700, textAlign: 'right' }}>{orgName || '—'}</span>
                </div>
                {sumRow('Product', `${P.label}${product === 'lorry' ? ` · ${lorryMode === 'cargo' ? 'Cargo' : 'Personal'}` : ''}`)}
                {sumRow('Plan', `${planTitle(billPlan)}${intent === 'trial' ? ' · after pilot' : ''}`)}
                {sumRow(isSchool ? 'Children' : 'Vehicles', size.toLocaleString())}
                {sumRow('Billing', billingLabel)}
                {addonCount > 0 && sumRow(`Add-ons · ${addonCount}`, addonList)}
                {intent === 'trial' && sumRow('Pilot ends', pilotEnds, true)}
                {intent === 'trial' && sumRow('First bill', firstBill, true)}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 22, color: '#94a3b8', fontSize: 12.5 }}>
                <FaLock style={{ fontSize: 11 }} /> Secured by Paystack
                <span style={{ color: '#cbd5e1' }}>·</span>
                <FaCreditCard style={{ fontSize: 12 }} /> Card
                <span style={{ color: '#cbd5e1' }}>·</span>
                <FaPhoneAlt style={{ fontSize: 11 }} /> M-PESA
              </div>
            </div>
          </div>

          {/* RIGHT — the pay CTA, then a skeleton while the secure Paystack window
              (resumeTransaction) opens over this branded split screen. */}
          <div style={{ background: '#fff', padding: '46px clamp(24px,5vw,64px)', display: 'grid', alignContent: 'center' }}>
            <div style={{ maxWidth: 400, width: '100%' }}>
              {/* Idle: the pay button + promo. */}
              {!busy && (
                <>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>Payment</div>
                  <div style={{ fontSize: 13.5, color: '#8a93a2', lineHeight: 1.55, marginBottom: 14 }}>
                    {intent === 'trial'
                      ? `A small ${money(50, currency)} card check is taken and refunded immediately — ${money(0, currency)} net today. Your card details never touch our servers.`
                      : 'Complete payment securely below. Your card details never touch our servers.'}
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#8a93a2', letterSpacing: '.04em', marginBottom: 8 }}>BILL IN</div>
                    {currencyToggle}
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 8, lineHeight: 1.45 }}>
                      This sticks for renewals. Update payment later keeps the same currency.
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, margin: '0 0 16px', padding: '12px 14px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e7ecf3' }}>
                    <input
                      type="checkbox"
                      id="onboarding-terms"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      style={{ marginTop: 2, width: 16, height: 16, cursor: 'pointer', flexShrink: 0, accentColor: '#2f7cf0' }}
                    />
                    <label htmlFor="onboarding-terms" style={{ fontSize: 12.5, color: '#5b6b82', lineHeight: 1.5, cursor: 'pointer' }}>
                      I agree to the{' '}
                      <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#2f7cf0', textDecoration: 'none' }}>Terms of Service</a>
                      {' '}and{' '}
                      <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#2f7cf0', textDecoration: 'none' }}>Privacy Policy</a>
                      {intent === 'trial'
                        ? `. My ${PILOT_LENGTH_LABEL} pilot is free; when it ends my saved payment method is charged for the ${planTitle(billPlan)} plan unless I cancel first.`
                        : '.'}
                    </label>
                  </div>

                  <button type="button" disabled={sending || !termsAccepted} onClick={handleStart}
                    style={{ width: '100%', background: 'linear-gradient(135deg,#22c3ee,#2f7cf0)', color: '#fff', border: 'none', borderRadius: 12, padding: '15px 18px', fontSize: 15.5, fontWeight: 800, cursor: (sending || !termsAccepted) ? 'default' : 'pointer', opacity: !termsAccepted ? 0.55 : 1, boxShadow: '0 14px 30px -10px rgba(47,124,240,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <FaLock style={{ fontSize: 13 }} />
                    {intent === 'trial' ? `Start my pilot · ${money(0, currency)} today` : `Pay ${money(due, currency)}`}
                  </button>

                  <div style={{ marginTop: 14, textAlign: 'center' }}>
                    {!showPromo ? (
                      <button type="button" onClick={() => setShowPromo(true)}
                        style={{ background: 'none', border: 'none', color: '#2f7cf0', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 4 }}>
                        Have a promo code?
                      </button>
                    ) : (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input value={couponCode} autoFocus
                          onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponInfo(null); }}
                          onBlur={checkCoupon} placeholder="PEARTEST-AB12C"
                          style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: '1px solid #d8dee9', fontSize: 14, fontFamily: 'inherit', letterSpacing: 0.5, textTransform: 'uppercase' }} />
                        <button type="button" onClick={checkCoupon} disabled={couponChecking || !couponCode.trim()}
                          style={{ padding: '0 16px', borderRadius: 10, border: '1px solid #d8dee9', background: '#f8fafc', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                          {couponChecking ? 'Checking…' : 'Apply'}
                        </button>
                      </div>
                    )}
                    {couponInfo && (
                      <div style={{ marginTop: 8, fontSize: 12.5, fontWeight: 600, color: couponInfo.valid ? '#15803d' : '#b91c1c' }}>
                        {couponInfo.valid ? `✓ Applied — you'll pay ${money(couponInfo.amountKES ?? 0, currency)} at checkout.` : '✗ Coupon is invalid or expired.'}
                      </div>
                    )}
                  </div>

                  {intent === 'trial' && (
                    <div style={{ fontSize: 12, color: '#9aa4b2', marginTop: 18, lineHeight: 1.5, textAlign: 'center' }}>
                      Cancel anytime before {firstBill} and you’re never charged.
                    </div>
                  )}
                </>
              )}

              {/* Busy: skeleton while we open the secure Paystack window. */}
              {busy && (
                <div aria-live="polite">
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>Opening secure checkout…</div>
                  <div style={{ fontSize: 13.5, color: '#8a93a2', marginBottom: 22 }}>Bringing up your card &amp; M-PESA options.</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {skelBar('100%')}
                    {skelBar('100%')}
                    <div style={{ display: 'flex', gap: 12 }}>{skelBar('50%')}{skelBar('50%')}</div>
                    <div className="pay-skel" style={{ height: 48, width: '100%', borderRadius: 12, marginTop: 8 }} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="site-theme v3" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {Nav}
      <div className="flow">
        <aside className="flow-rail">
          <div className="fr-eyebrow">{railEyebrow}</div>
          <h2>{railTitle}</h2>
          <div className="fr-sub">Six quick steps. We size your plan as you go — no charge until you confirm.</div>
          <div className="steps">
            {STEPS.map((label, i) => {
              const n = i + 1;
              return (
                <button key={label} className={`step${step === n ? ' on' : ''}${step > n ? ' done' : ''}`} type="button" onClick={() => go(n)}>
                  <span className="sn">{step > n ? <FaCheck /> : n}</span>
                  {label}
                </button>
              );
            })}
          </div>

          {/* Running estimate — always the plan they PICKED (raised by any upgrade
              they switched on), never a guess, so the rail and checkout agree. */}
          <div className="fr-card">
            <div className="frk" style={{ marginBottom: 10 }}>Running estimate</div>
            <div className="frrow">
              <span><b className="fr-plan">{planTitle(billPlan)}</b> · {size.toLocaleString()} {unitWordP}</span>
            </div>
            {addonCount > 0 && (
              <div className="frrow fr-addons">
                <span>＋Ksh {fmt(addonRate(addonCount))} / {unitWord(unit, 1)} · {perUnitSuffix(unit)} in add-ons</span>
              </div>
            )}
            <div className="frrow"><span>{railPeriodLabel}</span><b className="frtotal">{money(total, currency)}</b></div>
            {intent === 'trial' && (
              <>
                <div className="frrow"><span style={{ color: 'var(--leaf-deep)', fontWeight: 700 }}>Due today</span><b>{money(0, currency)}</b></div>
                <div className="fr-foot">Free until {pilotEnds} · first charge {firstBill}</div>
              </>
            )}
          </div>
        </aside>

        <main className="flow-main">
          {renderStep()}
          <div className="flow-actions">
            <button className="btn btn-ghost" type="button" onClick={() => (step === 1 ? navigate('/pricing') : go(step - 1))}>
              <FaArrowLeft /> {step === 1 ? 'Pricing' : 'Back'}
            </button>
            {step < lastStep
              ? <button className="btn btn-primary" type="button" disabled={step === 2 && !orgStepValid} onClick={() => { if (step === 2 && !orgStepValid) return; go(step + 1); }}>Continue <FaArrowRight /></button>
              : (
                <button className="btn btn-primary" type="button" disabled={sending} onClick={handleStart}>
                  {paymentPhase === 'processing' ? 'Processing payment…' : paymentPhase === 'done' ? 'Sending email…' : intent === 'trial' ? 'Start my pilot' : 'Confirm & pay'} <FaCheck />
                </button>
              )}
          </div>
        </main>
      </div>
    </div>
  );
}
